import React, { useCallback, useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import { LTDrawer } from '@lambdatestincprivate/lt-components';
import { Portal } from '@primer/react';
import RCABanner from './RCABanner';
import RCACompactTrigger from './RCACompactTrigger';
import RCARowTrigger from './RCARowTrigger';
import RCADrawerContent from './RCADrawerContent';
import useRCA from './useRCA';
import { CreditSDK } from '@lambdatestincprivate/credit-sdk';
import { getProductRCA, isRCASupportedFramework } from './helpers';
import { FAILURE_STATUSES } from './constants';
import { RCA_EVENTS } from './events';

const AIRca = forwardRef(({
  testId,
  status,
  testName,
  subTitle,
  createdAt,
  rcaCategory,
  frameworkType,
  rawType,
  apiBaseUrl,
  userId,
  cookieName,
  product: productProp,
  loading,
  onDrawerClose,
  onTriggerClick,
  extraQueryParams,
  extraAnalyticsParams,
  triggerIcon,
  showGenerate,
  externalError,
  onClearExternalError,
  bannerMode,
  aiCapabilityEnabled = true,
  baseUrl,
  totalTests,
  activeTestIndex,
  onTestNavigate,
  onViewFailedStep,
  onEvent,
}, ref) => {
  const drawerRef = useRef(null);
  const pendingOpenRef = useRef(false);

  const isHyperExecute = productProp === AIRca.Products.HYPEREXECUTE;
  const isTestManager = productProp === AIRca.Products.TEST_MANAGER;
  const isFailedTest = isHyperExecute || isTestManager || FAILURE_STATUSES.includes(status);
  const isSupported =
    isHyperExecute || isTestManager || isRCASupportedFramework(frameworkType, rawType);
  // TEST_MANAGER is a UI mode, not an API product — resolve actual product from testId
  const product = (isTestManager || !productProp) ? getProductRCA({ testId }) : productProp;

  let triggerType = 'banner';
  if (isHyperExecute) triggerType = 'compact';
  else if (isTestManager && !bannerMode) triggerType = 'row';

  // Single analytics dispatcher — stamps product/test_id on every AIRca event.
  const track = useCallback(
    (name, params) => onEvent?.(name, { product, test_id: testId, ...params }),
    [onEvent, product, testId]
  );

  const {
    rcaData,
    isLoading,
    isGenerating,
    isError,
    errorMessage,
    isNotEnoughCredit,
    dismissNotEnoughCredit,
    errorRepetitionData,
    errorTrendsData,
    triggerRCA,
    fetchExistingRCA,
    refetch,
    submitFeedback,
  } = useRCA({
    testId,
    product,
    apiBaseUrl,
    userId,
    testName,
    createdAt,
    rcaCategory,
    cookieName,
    extraQueryParams,
    extraAnalyticsParams,
    isTestManager,
    onTrack: track,
    // Auto-fetch existing RCA on mount for standard failed tests, and for the
    // TEST_MANAGER banner (test-instance page) so it reflects a real RCA when
    // one exists instead of trusting the stale instance-level rca_category.
    enabled:
      (!isHyperExecute && !isTestManager && isFailedTest && isSupported && !!testId) ||
      (isTestManager && bannerMode && isFailedTest && !!testId),
  });

  // Deferred open — when testId resolves after trigger click
  // Checks for existing RCA so user can generate if needed.
  useEffect(() => {
    if (pendingOpenRef.current && testId) {
      pendingOpenRef.current = false;
      track(RCA_EVENTS.DRAWER_OPENED, { trigger_source: 'trigger' });
      drawerRef.current?.openDrawer();
      fetchExistingRCA();
    }
  }, [testId, fetchExistingRCA, track]);

  // Auto-fetch when testId changes during navigation (HyperExecute/TestManager)
  const prevTestIdRef = useRef(testId);
  useEffect(() => {
    const needToFetch =
      (isHyperExecute || isTestManager) &&
      testId &&
      prevTestIdRef.current &&
      testId !== prevTestIdRef.current;

    if (needToFetch) {
      fetchExistingRCA();
    }
    prevTestIdRef.current = testId;
  }, [testId, isHyperExecute, isTestManager, fetchExistingRCA]);

  // Opens the drawer and logs the open with its originating source.
  const openDrawer = useCallback(
    (source) => {
      track(RCA_EVENTS.DRAWER_OPENED, { trigger_source: source });
      drawerRef.current?.openDrawer();
    },
    [track]
  );

  const handleViewRCA = useCallback(() => {
    track(RCA_EVENTS.TRIGGER_CLICKED, { trigger_type: triggerType, action: 'view' });
    openDrawer('view');
    // Fetch existing RCA if not already loaded (e.g. TEST_MANAGER where enabled=false)
    if (!rcaData) {
      fetchExistingRCA();
    }
  }, [fetchExistingRCA, rcaData, track, triggerType, openDrawer]);

  const handleGenerateRCA = useCallback(() => {
    track(RCA_EVENTS.TRIGGER_CLICKED, { trigger_type: triggerType, action: 'generate' });
    openDrawer('generate');
    triggerRCA();
  }, [triggerRCA, track, triggerType, openDrawer]);

  // Re-running an existing/failed RCA forces a fresh generation (retry=true)
  // rather than re-reading the cached one.
  const handleRetry = useCallback(() => {
    track(RCA_EVENTS.RETRY_CLICKED, {
      previous_category: rcaData?.parent_failure_category,
    });
    triggerRCA({ retry: true });
  }, [triggerRCA, track, rcaData]);

  const handleClose = useCallback(() => {
    track(RCA_EVENTS.DRAWER_CLOSED, {
      had_rca_data: !!rcaData,
      feedback_provided: !!rcaData?.feedback_provided,
    });
    drawerRef.current?.closeDrawer();
    onClearExternalError?.();
    onDrawerClose?.();
  }, [onDrawerClose, onClearExternalError, track, rcaData]);

  const handleTriggerClick = useCallback(
    (e) => {
      e?.stopPropagation?.();
      onTriggerClick?.();
      track(RCA_EVENTS.TRIGGER_CLICKED, { trigger_type: triggerType, action: 'trigger' });
      if (testId) {
        openDrawer('trigger');
        if (isHyperExecute) {
          fetchExistingRCA();
        } else {
          triggerRCA();
        }
      } else {
        pendingOpenRef.current = true;
      }
    },
    [testId, onTriggerClick, isHyperExecute, fetchExistingRCA, triggerRCA, track, triggerType, openDrawer]
  );

  const handleDismissCredit = useCallback(() => {
    dismissNotEnoughCredit();
  }, [dismissNotEnoughCredit]);

  // Close drawer when credits are insufficient — only show the modal
  useEffect(() => {
    if (isNotEnoughCredit) {
      drawerRef.current?.closeDrawer();
    }
  }, [isNotEnoughCredit]);

  // Open drawer when external error is set (e.g. Test ID Not Found)
  useEffect(() => {
    if (externalError) {
      drawerRef.current?.openDrawer();
    }
  }, [externalError]);

  useImperativeHandle(ref, () => ({
    openDrawer: handleViewRCA,
  }), [handleViewRCA]);

  // Show loading while useRCA is fetching OR testId is still being resolved
  const combinedLoading = isLoading || (!!loading && !testId);

  // For HyperExecute/TestManager, allow rendering without testId (trigger still shows)
  const willRender = !(
    (!isHyperExecute && !isTestManager && (!isFailedTest || !isSupported || !testId)) ||
    ((isHyperExecute || isTestManager) && !isFailedTest)
  );

  // Whether a visible trigger renders — mirrors child null-guards so list rows that show nothing don't fire trigger_shown.
  const bannerCategory =
    rcaData?.parent_failure_category || rcaData?.root_cause_category || rcaCategory;
  const bannerHasData = !!(bannerCategory || rcaData?.failure_summary);
  const canGenerate = aiCapabilityEnabled && (!isTestManager || showGenerate);
  let triggerVisible;
  if (isHyperExecute) {
    triggerVisible = true; // compact icon always renders
  } else if (isTestManager && !bannerMode) {
    triggerVisible = !!rcaCategory || (showGenerate && aiCapabilityEnabled); // RCARowTrigger
  } else {
    // RCABanner: count a real entry point (data or generate), not the loading skeleton.
    triggerVisible = bannerHasData || canGenerate;
  }

  // Fire once per test (reset on testId change), after the existence fetch settles so has_existing_rca is accurate.
  const shownRef = useRef(false);
  const shownTestIdRef = useRef(testId);
  useEffect(() => {
    if (testId !== shownTestIdRef.current) {
      shownTestIdRef.current = testId;
      shownRef.current = false;
    }
    if (willRender && triggerVisible && !combinedLoading && !shownRef.current) {
      shownRef.current = true;
      track(RCA_EVENTS.TRIGGER_SHOWN, {
        trigger_type: triggerType,
        has_existing_rca: !!(rcaData || rcaCategory),
      });
    }
  }, [testId, willRender, triggerVisible, combinedLoading, track, triggerType, rcaData, rcaCategory]);

  if (!willRender) {
    return null;
  }

  return (
    <>
      {isHyperExecute ? (
        <RCACompactTrigger
          rcaCategory={rcaCategory}
          icon={triggerIcon}
          onClick={handleTriggerClick}
        />
      ) : isTestManager && !bannerMode ? (
        <RCARowTrigger
          rcaCategory={rcaCategory}
          isLoading={combinedLoading}
          onGenerateRCA={handleGenerateRCA}
          onViewRCA={handleViewRCA}
          showGenerate={showGenerate && aiCapabilityEnabled}
        />
      ) : (
        <RCABanner
          rcaData={rcaData}
          rcaCategory={isTestManager ? rcaCategory : undefined}
          isLoading={combinedLoading}
          onViewRCA={handleViewRCA}
          onGenerateRCA={
            aiCapabilityEnabled && (!isTestManager || showGenerate) ? handleGenerateRCA : undefined
          }
          onViewFailedStep={onViewFailedStep}
        />
      )}
      <Portal>
      <LTDrawer
        ref={drawerRef}
        title=""
        width="800px"
        closable={false}
        noPortal
        position={LTDrawer.Position.RIGHT}
        content={
          <RCADrawerContent
            isLoading={combinedLoading}
            isGenerating={isGenerating}
            isError={isError || !!externalError}
            errorTitle={externalError ? 'Test ID Not Found' : undefined}
            errorMessage={externalError ? externalError : undefined}
            errorType={externalError && 'warning'}
            rcaData={rcaData}
            testName={testName}
            subTitle={subTitle}
            status={status}
            testId={testId}
            product={product}
            errorRepetitionData={errorRepetitionData}
            errorTrendsData={errorTrendsData}
            onClose={handleClose}
            onRetry={externalError ? undefined : handleRetry}
            onRefetch={refetch}
            onGenerate={triggerRCA}
            submitFeedback={submitFeedback}
            rcaCategory={rcaCategory}
            baseUrl={baseUrl}
            totalTests={totalTests}
            activeTestIndex={activeTestIndex}
            onTestNavigate={onTestNavigate}
            onTrack={track}
          />
        }
        onClose={handleClose}
        bodyStyle={{ padding: '0px' }}
        headerStyle={{ display: 'none' }}
      />
      {isNotEnoughCredit && (
          <CreditSDK open onClose={handleDismissCredit} />
      )}
      </Portal>
    </>
  );
});

AIRca.Products = {
  HYPEREXECUTE: 'hyperexecute',
  APP_AUTOMATION: 'app-automation',
  WEB_AUTOMATION: 'web-automation',
  TEST_MANAGER: 'test-manager',
};

export default AIRca;
